import Header from '@/components/elements/Header/Header';
import { TERMS_URLS } from '@/constants/terms';
import { Button } from '@/components/elements/forms/buttons';
import ListInput from '@/components/elements/forms/listInput/ListInput/ListInput';
import Alert from '@/components/elements/notifications/Alert/Alert';
import Icon from '@/components/icons/Icon';
import { loginTrackingSession } from '@/helpers/loginTrackingSession';
import { LoginContextType } from '@/hooks/useLogin';
import { _s } from '@/locale';
import { _t } from '@/locale/i18n';
import { LoginSignupFlowInitiatedFrom } from '@/types/analytics';
import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

const baseTranslationKey = `components.templates.checkout.UserLogin`;

type UserLoginProps = Omit<
  LoginContextType,
  'handleLogin' | 'updateView' | 'view' | 'user' | 'handleEmailResendClicked' | 'show' | 'onOpen' | 'onClose' | 'flow'
> & {
  initiatedFrom: LoginSignupFlowInitiatedFrom;
  continueAsGuest?: () => void;
  /**
   * Popup mode (booking widget): no e-mail field — the CTA opens the
   * first-party login popup. Must stay synchronous (Safari gesture rule).
   */
  onLoginPopup?: () => void;
};

const UserLogin = ({
  email,
  updateEmail,
  handleEmailSubmit,
  continueAsGuest,
  initiatedFrom,
  clearLoginState,
  onAbortServerRequest,
  onLoginPopup,
}: UserLoginProps) => {
  const { pathname } = useLocation();
  const allowsContinueAsGuest = continueAsGuest !== undefined;
  const popupMode = onLoginPopup !== undefined;
  // Popup mode has no e-mail field, so "enter your e-mail" would be wrong — use the plain "Log in" title.
  const titleTranslation = _t(`${baseTranslationKey}.title${allowsContinueAsGuest || popupMode ? 'AsGuest' : ''}`);

  const getCtaLabel = () => {
    if (!popupMode && email.isChecked && !email.isRegistered) {
      return _t(`register`);
    }
    return _t(`${baseTranslationKey}.loginCta${allowsContinueAsGuest ? 'AsGuest' : ''}`);
  };

  const handleLoginClick = () => {
    if (onLoginPopup) {
      onLoginPopup();
      return;
    }
    handleEmailSubmit({ redirectUrl: pathname });
  };

  const handleContinueAsGuestClick = () => {
    loginTrackingSession.track({ step: 'continue_as_guest', flow: 'login' });
    loginTrackingSession.end();
    onAbortServerRequest();
    clearLoginState();
    continueAsGuest();
  };

  const handleTermsClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    loginTrackingSession.track({ step: 'terms_and_conditions', flow: 'login' });
  };

  useEffect(() => {
    if (loginTrackingSession.getInitiatedFrom() !== initiatedFrom) {
      loginTrackingSession.start(initiatedFrom, { force: true });
      loginTrackingSession.track({ step: 'initiated', flow: 'login' });
    }
  }, []);

  return (
    <div className="gap-md px-lg flex flex-col">
      <div className="py-md">
        <Header size="md" label={titleTranslation} />
      </div>
      <div className="gap-md flex flex-col">
        {!popupMode && (
          <ListInput
            id="email"
            type="email"
            autoComplete="email"
            value={email.value}
            label={_s(`${baseTranslationKey}.input.label`)}
            placeholder="namn@exempel.com"
            leftIcon={<Icon variant="email" />}
            onChange={(e) => updateEmail(e.target.value)}
            error={email.error && email.submitting ? _s(`RequestToken.input.error`) : undefined}
            floatStyle
            leftPadding={false}
            rightPadding={false}
          />
        )}
        {!popupMode && email.isChecked && !email.isRegistered && (
          <Alert
            leftSlot={<Icon size="sm" variant="info-circle" />}
            variant="information"
            verticalAlign="top"
            middleSlot={
              <p>
                {_s(`RequestToken.newEmailAccount.notFound`)} <strong>{email.value}</strong>
              </p>
            }
          />
        )}
        <Button
          disabled={email.error || email.submitting}
          label={getCtaLabel()}
          onClick={handleLoginClick}
          size="md"
          variant="primary"
        />
        {allowsContinueAsGuest && (
          <Button
            label={_s(`${baseTranslationKey}.continueAsGuest`)}
            onClick={handleContinueAsGuestClick}
            size="sm"
            variant="link"
          />
        )}
        <p className="text-black-600 text-m">
          {_s(`RequestToken.terms.text`)}
          <br />
          <a onClick={handleTermsClick} href={TERMS_URLS.USAGE} target="_blank" rel="noreferrer" className="underline">
            {_s(`RequestToken.terms.link`)}
          </a>
        </p>
      </div>
    </div>
  );
};

export default UserLogin;
